<template>
  <Dashboard title="Training Sessions" :no-padding="user.access_level < 1">
    <div v-if="showUpgrade" class="flex flex-col gap-4">
      <div class="w-full h-full bg-gray-950 relative flex justify-center items-center">
        <img class="select-none w-full h-full object-cover" src="../../assets/demo-viewer-training-sessions.png" />
        <div class="w-full h-full bg-black/60 z-50 absolute items-center justify-center flex">
          <UpgradeModal
            :title="$t('training.sessions.upgrade_title')"
            tier="Player"
            :firstBenefit="$t('training.sessions.upgrade_benefit_history')"
            :secondBenefit="$t('training.sessions.upgrade_benefit_progress')"
            primaryPromotionType="training_sessions"
            video_url=""
          />
          <!-- TODO(ssaunier): Add the video_url for the new Youtube Video explaining this new feature -->
        </div>
      </div>
    </div>

    <div v-else class="flex flex-col gap-2 h-full w-full">
      <!-- Header -->
      <h1 class="text-text-primary text-[28px] font-medium flex-grow flex items-center gap-2">
        {{ $t('training.sessions.timeline') }}
      </h1>

      <div class="bg-gray-900 rounded-lg p-2 mb-3 h-[180px]" v-if="trainingSessionsLoading" />
      <div class="bg-gray-900 rounded-lg p-2" v-else-if="trainingSessions?.length > 0">
        <StatisticCharts :statistics="statistics" @selectedMetricChange="selectedMetricChange" :singleSession="false">
          <MapDropdown ref="mapDropdownRef" @selectedMapChange="loadTrainingSessions" />
          <ModDropdown ref="modDropdownRef" @selectedModChange="loadTrainingSessions" :mods="mods" />
        </StatisticCharts>
      </div>

      <!-- Body -->
      <TrainingSessionsSkeleton v-if="trainingSessionsLoading" as="div" />
      <div v-else-if="trainingSessionsError" class="text-white">
        <div class="bg-gray-900 p-4 text-text-secondary text-xs tracking-[1.5px]">
          {{ $t('training.sessions.error') }}
        </div>
      </div>
      <div v-else-if="trainingSessions.length == 0" class="text-white">
        <div class="bg-gray-900 p-4 text-text-secondary text-xs tracking-[1.5px]">
          {{ $t('training.sessions.empty') }}
          <router-link
            v-if="(mapDropdownRef === null && route.query.map?.length > 0) || (modDropdownRef === null && route.query.mod?.length > 0)"
            class="underline"
            to="/sessions"
            @click="() => loadTrainingSessions(1)"
          >
            {{ $t('training.sessions.remove_filters') }}
          </router-link>
          <i18n-t scope="global" v-else keypath="training.sessions.empty_hint" tag="span">
            <template #coach><router-link class="underline" to="/coach">{{ $t('training.sessions.empty_hint_coach') }}</router-link></template>
            <template #routine><router-link class="underline" to="/routines">{{ $t('training.sessions.empty_hint_routine') }}</router-link></template>
            <template #open_practice><router-link class="underline" to="/play">{{ $t('training.sessions.empty_hint_open_practice') }}</router-link></template>
          </i18n-t>
        </div>
      </div>
      <div v-else>
        <div v-for="trainingSession in trainingSessions" :key="trainingSession.uuid" class="mb-3 rounded-lg bg-gray-900 overflow-hidden">
          <TrainingSessionRow
            :trainingSession="trainingSession"
            :visible="!!trainingSessionsVisible[trainingSession.uuid]"
            :loaded="!!trainingSessionsLoaded[trainingSession.uuid]"
            :modCategories="modCategories"
            :selectedMetric="selectedMetric"
            @toggleRow="toggleTrainingSessionRow"
          />
        </div>

        <Pagination
          :itemName="$t('training.sessions.item_name')"
          :currentPage="pagination.current_page"
          :perPage="pagination.per_page"
          :totalCount="pagination.total_count"
          :totalPages="pagination.total_pages"
          @changePage="loadTrainingSessions"
        />
      </div>
    </div>
  </Dashboard>
</template>

<script setup>
import axios from 'axios';
import {
  ref, onMounted, onBeforeUnmount, getCurrentInstance,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useHead } from '@unhead/vue';
import { useI18n } from 'vue-i18n';
import Dashboard from '../../components/layouts/DashboardLayout.vue';
import UpgradeModal from '../../components/UpgradeModal.vue';
import Pagination from '../../components/Pagination.vue';
import MapDropdown from '../../components/Filters/MapDropdown.vue';
import ModDropdown from '../../components/Filters/ModDropdown.vue';
import TrainingSessionsSkeleton from './TrainingSessionsSkeleton.vue';
import TrainingSessionRow from './TrainingSessionRow.vue';
import StatisticCharts from './StatisticCharts.vue';

const { t } = useI18n();
useHead({ title: t('training.sessions.page_title') });

const auth = useAuth();
const user = ref(auth.user());
const store = useStore();
const route = useRoute();
const router = useRouter();

const showUpgrade = ref(user.value?.access_level < 1); // Minimum access level: player tier
const trainingSessionsLoading = ref(true);
const trainingSessionsError = ref(false);
const trainingSessions = ref([]); // Data loaded from back-end
const pagination = ref({});
const statistics = ref({});
const trainingSessionsVisible = ref({}); // uuid => {true, false}
const trainingSessionsLoaded = ref({}); // uuid -> {true, false}
const modCategories = ref([]);
const selectedMetric = ref(null);

const mapDropdownRef = ref(null);
const mods = ref([]);
const modDropdownRef = ref(null);

const cable = getCurrentInstance().appContext.config.globalProperties.$cable;
let websocket = null;
let subscription = null;

function loadTrainingSessions(page = 1) {
  trainingSessionsLoaded.value = {};
  trainingSessionsVisible.value = {};
  axios({
    url: '/training_sessions',
    params: {
      page,
      map_name: mapDropdownRef.value?.selectedMap?.name,
      mod_name: modDropdownRef.value?.selectedMod?.name,
    },
    headers: {
      'X-TEAM-ID': store.state.activeTeam.id, // TODO(ssaunier): Would we show team's training sessions or only "me"?
    },
  }).then(({ data }) => {
    if (mapDropdownRef.value?.selectedMap) {
      useHead({ title: t('training.sessions.page_title_map', { map: mapDropdownRef.value?.selectedMap.friendly_name }) });
    } else {
      useHead({ title: t('training.sessions.page_title') });
    }

    trainingSessions.value = data.training_sessions;
    pagination.value = data.pagination;
    statistics.value = data.statistics;
    mods.value = data.mods;
    trainingSessionsError.value = false;
    trainingSessionsLoading.value = false;

    if (trainingSessions.value.length > 0) {
      const { uuid } = trainingSessions.value[0]; // Load & Show first training session of the page
      trainingSessionsVisible.value[uuid] = true;
      loadTrainingSessionDetails(uuid);
      if (data.pagination.current_page == 1 && trainingSessions.value[0].live) {
        subscribeToLiveTrainingSessionUpdate(trainingSessions.value[0].uuid);
      } else {
        unsubscribeToLiveTrainingSessionUpdate();
      }
    }
  }).catch((error) => {
    console.error('Error fetching training sessions data:', error);
    trainingSessionsError.value = true;
    trainingSessionsLoading.value = false;
  });
}

function loadTrainingSessionDetails(uuid, from_websocket = false) {
  axios({
    url: `/training_sessions/${uuid}`,
    params: {
      map_name: mapDropdownRef.value?.selectedMap?.name,
      mod_name: modDropdownRef.value?.selectedMod?.name,
    },
    headers: {
      'X-TEAM-ID': store.state.activeTeam.id,
    },
  }).then(({ data }) => {
    const index = trainingSessions.value.findIndex((s) => s.uuid === uuid);
    trainingSessions.value[index] = data;
    trainingSessionsLoaded.value[uuid] = true;
    if (from_websocket) {
      appendStatistics(data);
      if (!data.live) {
        unsubscribeToLiveTrainingSessionUpdate();
      }
    }
  }).catch((error) => {
    console.error(`Error fetching training session details data for uuid: ${uuid}`, error);
  });
}

function getModCategories() {
  axios('/mod_categories').then(({ data }) => {
    modCategories.value = data.mod_categories;
  });
}

function isTrainingSessionLoaded(uuid) {
  const index = trainingSessions.value.findIndex((s) => s.uuid === uuid);
  return typeof trainingSessions.value[index].activities !== 'undefined';
}

function toggleTrainingSessionRow(uuid) {
  trainingSessionsVisible.value[uuid] = !trainingSessionsVisible.value[uuid];
  if (trainingSessionsVisible.value[uuid] && !isTrainingSessionLoaded(uuid)) {
    loadTrainingSessionDetails(uuid);
  }
}

function subscribeToLiveTrainingSessionUpdate(uuid) {
  if (!websocket) {
    websocket = cable.connect();
  }
  if (!subscription) {
    subscription = websocket.subscriptions.create(
      { channel: 'TrainingSessionChannel', uuid },
      {
        received: (data) => {
          if (data.event === 'training_session_update') {
            if (!data.live && data.empty) {
              // NOTE(ssaunier): reload everything as we will hide the recent live training session which turned out empty
              loadTrainingSessions();
            } else {
              loadTrainingSessionDetails(uuid, true);
            }
          }
        },
      },
    );
  }
}

function unsubscribeToLiveTrainingSessionUpdate() {
  if (websocket) {
    websocket.disconnect();
    websocket = null;
  }
  subscription = null;
}

function appendStatistics(data) {
  data.activities.forEach((activity) => {
    activity.tasks.forEach((task) => {
      const stat = task.statistics[0]; // Taking only the primary weapon.
      if (stat && statistics.value.findIndex((s) => s.id === stat.id) === -1) {
        statistics.value.push(stat);
      }
    });
  });

  statistics.value = statistics.value.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
}

function selectedMetricChange(newMetric) {
  selectedMetric.value = newMetric;
}

onMounted(() => {
  if (!showUpgrade.value) {
    loadTrainingSessions();
    getModCategories();
  }
});

onBeforeUnmount(() => {
  unsubscribeToLiveTrainingSessionUpdate();
});
</script>
